// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Users, Plus, Download, Upload } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, EmptyState, SetupGuide, Skeleton, Pagination } from '@/components/ui'
import { ExportModal } from '@/components/export/ExportModal'
import { CustomerCard } from './components/CustomerCard'
import { CustomerFilters } from './components/CustomerFilters'
import { useCustomers } from '@/hooks/useCustomers'
import { useSetupScopeSession, useSupportSession } from '@/hooks/useSupportSession'
import { useAuth } from '@/hooks/useAuth'
import { useListSort } from '@/hooks/useListSort'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useAccountSettings } from '@/hooks/useAccountSettings'

export function CustomerListPage() {
  const { t } = useTranslation('customers')
  const { t: tc } = useTranslation('common')
  const supportSession = useSupportSession().active
  // EP-160: a setup session may not create a customer (422 scope_setup_no_tenants).
  const setupScope = useSetupScopeSession()
  const navigate = useNavigate()
  const featureFlags = useFeatureFlags()
  const { data: accountSettings } = useAccountSettings()
  const { user } = useAuth()
  // D2: Import is admin + owner only — staff still add customers individually.
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showExportModal, setShowExportModal] = useState(false)
  // EP-116 T031: this page deliberately ignores the global property scope —
  // see the exception list in useSelectedPropertyStore before "restoring" it.

  const showLeadGenFeatures =
    featureFlags.leadGeneration && (accountSettings?.settings.lead_generation_enabled ?? false)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  // Card grid (no table headers), so the sort control shows on all viewports.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  // EP-116 T031: no property_id — see the comment above on why this page
  // stopped scoping by property at all.
  const { data, isLoading, isFetching, error } = useCustomers(page, status || undefined, debouncedSearch || undefined, undefined, sortParams)

  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'name', label: tc('sort.by_name') },
    { value: 'created_at', label: tc('sort.by_newest') },
  ]

  const customers = data?.data

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: tc('nav.customers') }]} />

        <PageHeader
          title={t('title')}
          description={t('description')}
          isRefreshing={isFetching && !isLoading}
          primaryAction={{
            key: 'add-customer',
            label: t('actions.add_customer'),
            icon: Plus,
            onClick: () => navigate('/customers/new'),
            hidden: setupScope,
          }}
          overflowActions={[
            {
              key: 'export',
              label: tc('export_data.export_button'),
              icon: Download,
              onClick: () => setShowExportModal(true),
              // EP-160 S3: no CSV export in a FlowRent support session.
              hidden: supportSession,
            },
            // D2: Import + Download sample — admin/owner only. Routes to the
            // onboarding importer (EP-110 T110-3), replacing the legacy modal.
            {
              key: 'import',
              label: t('actions.import'),
              icon: Upload,
              onClick: () => navigate('/properties/import'),
              hidden: !isAdminOrOwner,
            },
            {
              key: 'download-sample',
              label: t('setup_guide.download_sample'),
              icon: Download,
              href: '/import-samples/customers.xlsx',
              download: true,
              hidden: !isAdminOrOwner,
            },
          ]}
        />

        {/* Customers list — ONE card wraps the filters (tabs + search + Recent
            heading + sort controls) and the card grid, matching the Figma
            "Search-Card" container (node 20-1030). */}
        <Card bodyClassName="space-y-6">
          <CustomerFilters
            status={status}
            onStatusChange={(v) => {
              setStatus(v)
              setPage(1)
            }}
            search={search}
            onSearchChange={setSearch}
            sort={sort}
            order={order}
            onSortChange={handleSort}
            onToggleOrder={handleToggleOrder}
            sortOptions={sortOptions}
            showExternalTab={showLeadGenFeatures}
          />

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-36" />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              title={tc('errors.something_went_wrong')}
              description={tc('errors.something_went_wrong')}
              action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
            />
          ) : !customers || customers.length === 0 ? (
            !search && !status ? (
              <SetupGuide
                icon={<Users className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                steps={[
                  { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                  { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
                ]}
                action={
                  setupScope ? undefined : (
                    <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/customers/new')}>
                      {t('actions.add_customer')}
                    </Button>
                  )
                }
              />
            ) : (
              <EmptyState
                icon={<Users className="w-12 h-12" />}
                title={t('empty.no_results')}
                description={t('empty.no_results')}
                action={
                  !search && !setupScope && (
                    <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/customers/new')}>
                      {t('actions.add_customer')}
                    </Button>
                  )
                }
              />
            )
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {customers.map((customer) => (
                <CustomerCard key={customer.id} customer={customer} />
              ))}
            </div>
          )}
        </Card>

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dataType="customers"
      />
    </>
  )
}
